// boodaVoice — JS-side wrapper around the native BoodaVoice plugin.
//
// Phase 1 surface only. The current useVoiceCommands.js still talks to
// @capacitor-community/speech-recognition; this wrapper is the target
// shape for the Phase 2 swap. Wiring it up now (instead of leaving it
// for later) lets the native plugin be smoke-tested in isolation —
// e.g. a Settings dev panel can call BoodaVoice.start() without
// touching the live cook-mode flow.
//
// On non-iOS (web, Android), every call resolves with a "not supported"
// shape so call sites can fall back without runtime checks scattered
// everywhere.

import { Capacitor, registerPlugin } from "@capacitor/core";

const isIOSNative =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

// registerPlugin returns a proxy that throws on unsupported platforms
// when methods are called. We always register so the proxy exists;
// the isIOSNative gate below decides whether to actually invoke.
const Native = registerPlugin("BoodaVoice");

const noop = () => Promise.resolve({ supported: false });

export const BoodaVoice = {
  /// True only on iOS native. Web / Android stay on the legacy path.
  supported: isIOSNative,

  checkPermissions: isIOSNative ? () => Native.checkPermissions() : noop,
  requestPermissions: isIOSNative ? () => Native.requestPermissions() : noop,
  start: isIOSNative
    ? (opts = {}) => Native.start({ language: opts.language ?? "en-US" })
    : noop,
  stop: isIOSNative ? () => Native.stop() : noop,

  playChime: isIOSNative
    ? (name, opts = {}) =>
        Native.playChime({
          name,
          repeatCount: Number.isFinite(opts.repeatCount) ? opts.repeatCount : 1,
        })
    : noop,
  stopChime: isIOSNative ? () => Native.stopChime() : noop,

  playNarration: isIOSNative
    ? (url) => Native.playNarration({ url })
    : noop,
  stopNarration: isIOSNative ? () => Native.stopNarration() : noop,
  pauseNarration: isIOSNative ? () => Native.pauseNarration() : noop,
  resumeNarration: isIOSNative ? () => Native.resumeNarration() : noop,

  // Background music — routed through the engine's musicPlayer node so
  // bg-music shares the iOS audio session with voice + narration. The
  // single pipeline (everything → mainMixer → outputNode) coexists
  // cleanly with VPIO + .playAndRecord, where HTML5 audio doesn't.
  // See BoodaVoiceEngine.swift for the player + session lifecycle.
  playMusicEnvelope: isIOSNative
    ? ({ introUrl, loopUrl, outroUrl, totalDurationSec }) =>
        Native.playMusicEnvelope({
          introUrl: String(introUrl),
          loopUrl:  String(loopUrl),
          outroUrl: String(outroUrl),
          totalDurationSec: Number(totalDurationSec) || 0,
        })
    : noop,
  playMusicLoop: isIOSNative
    ? ({ pt1Url, pt2Url }) =>
        Native.playMusicLoop({ pt1Url: String(pt1Url), pt2Url: String(pt2Url) })
    : noop,
  stopMusic: isIOSNative ? () => Native.stopMusic() : noop,
  setMusicVolume: isIOSNative
    ? (gain) => Native.setMusicVolume({ gain: Number(gain) || 0 })
    : noop,
  pauseMusic: isIOSNative ? () => Native.pauseMusic() : noop,
  resumeMusic: isIOSNative ? () => Native.resumeMusic() : noop,

  /// Subscribe to a plugin event. Returns a handle whose .remove()
  /// unsubscribes — same shape Capacitor.addListener returns. Pass-
  /// through for native; no-op handle for web/Android.
  addListener: isIOSNative
    ? (eventName, cb) => Native.addListener(eventName, cb)
    : () => Promise.resolve({ remove: () => {} }),
};

export default BoodaVoice;
